import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mill, DataLayerId, SupplyShedDriveTime } from '../../lib/types';
import { DATA_LAYERS } from '../../lib/dataLayers';
import { getMillBrief } from '../../lib/millsData';
import { LayerPickerListbox } from '../shared/LayerPickerListbox';
import { PanelToggleButton } from '../shared/PanelToggleButton';
import { ChromeBar } from '../shared/ChromeBar';
import { BrandLogo } from '../shared/BrandLogo';
import { SfiLogo } from '../shared/SfiLogo';
import { DemoBadge } from '../shared/DemoBadge';
import { SupplyShedToggle } from './SupplyShedToggle';
import { ShedBriefCard } from './ShedBriefCard';
import { ShedSummaryCard } from './ShedSummaryCard';
import { AgbContent } from './layers/AgbContent';
import { SoilCarbonContent } from './layers/SoilCarbonContent';
import { GrowthCurvesContent } from './layers/GrowthCurvesContent';
import { WildfireHazardContent } from './layers/WildfireHazardContent';
import { SpeciesContent } from './layers/SpeciesContent';
import { StandDensityContent } from './layers/StandDensityContent';
import { ForestOwnershipContent } from './layers/ForestOwnershipContent';
import { DeforestationAlertsContent } from './layers/DeforestationAlertsContent';
import { CanopyHeightContent } from './layers/CanopyHeightContent';
import { ImperiledSpeciesContent } from './layers/ImperiledSpeciesContent';
import { ResiliencePriorityContent } from './layers/ResiliencePriorityContent';
import { PerCrownContent } from './layers/PerCrownContent';
import { ComingSoonPlaceholder } from './layers/ComingSoonPlaceholder';
import { ScenarioPanel } from './ScenarioPanel';
import { UseScenarioResult } from '../../hooks/useScenario';
import { MobileBottomSheet } from '../shared/MobileBottomSheet';
import { FeedbackPanelLink } from '../shared/FeedbackPanelLink';
import { useIsMobile } from '../../hooks/useIsMobile';

interface DetailLeftPanelProps {
  mill: Mill;
  selectedLayer: DataLayerId | null;
  selectedDriveTime: SupplyShedDriveTime;
  onLayerChange: (layer: DataLayerId) => void;
  onDriveTimeChange: (time: SupplyShedDriveTime) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isochroneLoading: boolean;
  // Current year the AGB map overlay is showing; echoed in AgbContent so the
  // chart and the map stay visually linked while the user scrubs.
  agbYear: number;
  // Current depth profile (30 or 100 cm) for the soil-carbon overlay + panel.
  // Lifted to DetailView so the left-panel depth toggle and the map raster
  // swap together.
  soilCarbonDepth: 30 | 100;
  onSoilCarbonDepthChange: (depth: 30 | 100) => void;
  // Scenario Mode state. When `activeScenario` is set, the panel replaces
  // its layer-content slot with ScenarioPanel; the supply-shed toggle and
  // layer dropdown remain mounted so the presenter can exit mid-demo.
  // `exitToExplorer` is layered on top of the hook by DetailView because
  // routing (navigate to '/') is a route-aware concern that doesn't belong
  // inside the otherwise route-agnostic useScenario hook.
  scenario: UseScenarioResult & { exitToExplorer: () => void };
  // Native-resolution per-crown view state — read by the trigger card in
  // PerCrownContent. The button toggle and any banner-driven exit both
  // route through `onToggleNativeView`.
  nativeViewActive: boolean;
  onToggleNativeView: () => void;
  // Controlled mobile bottom-sheet state. Lifted to DetailView so other
  // surfaces (the scenario map overlay, native-view auto-collapse) can
  // drive open/close. Desktop ignores both fields.
  mobileSheetExpanded: boolean;
  onMobileSheetExpandedChange: (expanded: boolean) => void;
}

const renderLayerContent = (
  layerId: DataLayerId,
  millCode: string | null,
  driveTime: SupplyShedDriveTime,
  state: string,
  agbYear: number,
  soilCarbonDepth: 30 | 100,
  onSoilCarbonDepthChange: (depth: 30 | 100) => void,
  nativeViewActive: boolean,
  onToggleNativeView: () => void,
): React.ReactNode => {
  switch (layerId) {
    case 'aboveground_biomass':
      return <AgbContent millCode={millCode} driveTimeMinutes={driveTime} agbYear={agbYear} />;
    case 'soil_carbon':
      return (
        <SoilCarbonContent
          millCode={millCode}
          driveTimeMinutes={driveTime}
          depth={soilCarbonDepth}
          onDepthChange={onSoilCarbonDepthChange}
        />
      );
    case 'growth_curves':
      return <GrowthCurvesContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'fire_risk':
      return <WildfireHazardContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'species_diversity':
      return <SpeciesContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'stand_density':
      return <StandDensityContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'forest_ownership':
      return <ForestOwnershipContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'deforestation_alerts':
      return <DeforestationAlertsContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'canopy_height':
      return <CanopyHeightContent state={state} />;
    case 'imperiled_species':
      return <ImperiledSpeciesContent millCode={millCode} driveTimeMinutes={driveTime} />;
    case 'resilience_priority':
      return <ResiliencePriorityContent />;
    case 'tree_crown_segmentation':
      return (
        <PerCrownContent
          millCode={millCode}
          driveTimeMinutes={driveTime}
          nativeViewActive={nativeViewActive}
          onToggleNativeView={onToggleNativeView}
        />
      );
    default:
      return <ComingSoonPlaceholder layer={DATA_LAYERS[layerId]} />;
  }
};

export const DetailLeftPanel: React.FC<DetailLeftPanelProps> = ({
  mill,
  selectedLayer,
  selectedDriveTime,
  onLayerChange,
  onDriveTimeChange,
  isCollapsed,
  onToggleCollapse,
  isochroneLoading,
  agbYear,
  soilCarbonDepth,
  onSoilCarbonDepthChange,
  scenario,
  nativeViewActive,
  onToggleNativeView,
  mobileSheetExpanded,
  onMobileSheetExpandedChange,
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const millCode = (mill.metadata?.code as string | undefined) ?? null;

  // Auto-collapse the mobile bottom sheet when the user activates the
  // per-crown native view. Otherwise the cinematic fly-in happens while
  // the panel is still covering the viewport, and the user has to
  // manually tap "Map" to see the trees they just asked to see. Desktop
  // ignores this (sheet is `md:hidden`). The sheet's expanded state itself
  // is controlled by DetailView so other surfaces (scenario map overlay)
  // can drive it too.
  useEffect(() => {
    if (isMobile && nativeViewActive) {
      onMobileSheetExpandedChange(false);
    }
  }, [isMobile, nativeViewActive, onMobileSheetExpandedChange]);

  const handleBack = () => {
    // Return to ExplorerView. useMillSelection restores selectedState +
    // selectedMill from localStorage on mount, so the dropdowns reopen
    // with the user's prior selection. Pass `fromDetail` in router state
    // so LeftPanel can scroll the mill picker into view — otherwise the
    // panel resets to top with scenarios prominent and the user has to
    // hunt for their just-explored mill.
    navigate('/', { state: { fromDetail: true } });
  };

  // Body content used by both the desktop side-rail and the mobile bottom
  // sheet. Identical markup so layout/copy changes flow to both surfaces.
  const body = (
    <div className="p-5 sm:p-6 space-y-6">
      {/* Header — logo left, "← BACK" right */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <BrandLogo size={28} />
          <span aria-hidden="true" className="h-5 w-px bg-figma-ink-mute/40" />
          <SfiLogo size={28} />
          <DemoBadge />
        </div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 bg-transparent border-0 cursor-pointer text-figma-text font-roboto font-bold text-[12px] uppercase tracking-[0.08em] hover:text-figma-lime transition-colors min-h-[44px] px-2 -mr-2"
          aria-label="Back to Explorer"
          title="Back to Explorer"
        >
          <span aria-hidden="true">←</span>
          Back
        </button>
      </div>

      {/* Mill identity block. Anton headline scales down on small screens
          so a two-word display name like "Mill City" doesn't blow out a
          375 px viewport. */}
      <div className="flex flex-col gap-2">
        <span className="text-[11px] font-roboto font-bold uppercase tracking-[0.16em] text-figma-ink-mute">
          {mill.state}
        </span>
        <h2 className="font-anton font-normal text-[30px] sm:text-[40px] leading-[1.05] tracking-[0.005em] uppercase text-figma-text m-0 break-words">
          {mill.display_name}
        </h2>
        <p className="text-[11px] font-roboto-mono text-figma-ink-mute mt-1">
          {mill.latitude.toFixed(2)}°N, {Math.abs(mill.longitude).toFixed(2)}°W
        </p>
        {mill.metadata?.description && (
          <p className="text-[13px] font-roboto text-figma-ink-soft leading-relaxed mt-1">
            {mill.metadata.description}
          </p>
        )}
        <ShedBriefCard brief={getMillBrief(mill)} />
        {/* Re-entry into guided mode from inside DetailView. Hidden while
            a scenario is already running so the link doesn't compete
            with the scenario-panel exit affordances. Routes via the
            ?mode=scenario nudge so Explorer scrolls + pulses the
            scenario selector on arrival — see ExplorerView. */}
        {!scenario.activeScenario && (
          <button
            type="button"
            onClick={() => navigate('/?mode=scenario')}
            className="mt-3 self-start text-left text-[13px] font-roboto text-figma-yellow hover:text-figma-lime focus:outline-none focus-visible:underline transition-smooth"
          >
            Run a guided scenario &rarr;
          </button>
        )}
      </div>

      {/* Supply Shed Toggle */}
      <div className="border-t border-figma-lime/20 pt-6">
        <SupplyShedToggle
          selectedDriveTime={selectedDriveTime}
          onDriveTimeChange={onDriveTimeChange}
          isLoading={isochroneLoading}
        />
      </div>

      {/* "At a glance" summary — composes the per-layer stats hooks into
          a 6-7 tile grid above the layer picker. Auto-collapses to the
          header strip the first time the user picks a layer so the
          panel space goes back to layer content; chevron in the header
          toggles. Hidden while a scenario is running because the
          scenario controls layer selection and the summary's clickable
          tiles would compete with that. */}
      {!scenario.activeScenario && (
        <ShedSummaryCard
          millCode={millCode}
          driveTime={selectedDriveTime}
          selectedLayer={selectedLayer}
          onLayerChange={onLayerChange}
        />
      )}

      {/* Data Layer Selection — disabled while a scenario is running
          because the scenario fully controls the active layer set.
          Hiding it would cause the panel to jump on entry/exit; greying
          it out keeps the spatial rhythm. The "Locked by scenario"
          hint + End-scenario CTA below makes the disabled state
          self-explaining and surfaces the free-explore off-ramp inline
          (rather than relying on the user to discover the matching
          link in ScenarioPanel's header). */}
      <div className="border-t border-figma-lime/20 pt-6 space-y-2">
        <div className="flex items-baseline justify-between gap-2">
          <label className="block text-body font-roboto font-medium text-figma-text">
            Select Data Layer:
          </label>
          {scenario.activeScenario && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-roboto-mono uppercase tracking-wider text-figma-yellow/90"
              aria-hidden="true"
            >
              <svg width="9" height="11" viewBox="0 0 9 11" fill="none">
                <path
                  d="M2 5V3.5a2.5 2.5 0 1 1 5 0V5"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
                <rect x="1" y="5" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.2" />
              </svg>
              Locked by scenario
            </span>
          )}
        </div>
        <LayerPickerListbox
          selectedLayer={selectedLayer}
          onLayerChange={onLayerChange}
          millCode={millCode}
          driveTime={selectedDriveTime}
          variant="wide"
          disabled={scenario.activeScenario !== null}
        />
        {scenario.activeScenario && (
          <p className="text-xs font-roboto text-neutral-400 leading-relaxed">
            The active scenario is driving the layer.{' '}
            <button
              type="button"
              onClick={scenario.exit}
              className="text-figma-yellow hover:text-figma-lime focus:outline-none focus-visible:underline transition-smooth"
            >
              End scenario to free-explore &rarr;
            </button>
          </p>
        )}
      </div>

      {/* Scenario Mode mounts the ScenarioPanel (narrative + nav +
          optional insight) above the primary-layer's content component.
          Showing layer content alongside the narrative gives the user
          the data substance the docent voice is referring to (e.g.
          "scrub the year slider" + the AGB chart in the same view).
          On the insight step the insight card *is* the data substance,
          so we suppress the duplicate layer-content block there. In
          free exploration the slot falls back to the user-picked
          layer's content as before. */}
      {scenario.activeScenario && scenario.currentStep ? (
        <>
          <div className="border-t border-figma-lime/20 pt-6">
            <ScenarioPanel
              scenario={scenario.activeScenario}
              step={scenario.currentStep}
              stepIndex={scenario.stepIndex}
              totalSteps={scenario.totalSteps}
              isFirstStep={scenario.isFirstStep}
              isLastStep={scenario.isLastStep}
              millCode={millCode}
              driveTime={selectedDriveTime}
              onPrev={scenario.prev}
              onNext={scenario.next}
              onExit={scenario.exit}
              onExitToExplorer={scenario.exitToExplorer}
              onJumpToStep={scenario.goToStep}
              nativeViewActive={nativeViewActive}
              onToggleNativeView={onToggleNativeView}
            />
          </div>
          {!scenario.currentStep.insight && selectedLayer && (
            <div className="border-t border-figma-lime/20 pt-6">
              {renderLayerContent(
                selectedLayer,
                millCode,
                selectedDriveTime,
                mill.state,
                agbYear,
                soilCarbonDepth,
                onSoilCarbonDepthChange,
                nativeViewActive,
                onToggleNativeView,
              )}
            </div>
          )}
        </>
      ) : selectedLayer ? (
        <div className="border-t border-figma-lime/20 pt-6">
          {renderLayerContent(
            selectedLayer,
            millCode,
            selectedDriveTime,
            mill.state,
            agbYear,
            soilCarbonDepth,
            onSoilCarbonDepthChange,
            nativeViewActive,
            onToggleNativeView,
          )}
        </div>
      ) : null}

      <FeedbackPanelLink />
    </div>
  );

  // Mobile: bottom sheet. Peek bar shows the active layer name (the user's
  // current question — "what am I looking at") with the mill name as the
  // eyebrow context. isCollapsed is desktop-only so the mobile path
  // ignores it.
  if (isMobile) {
    const activeLayerLabel = selectedLayer
      ? DATA_LAYERS[selectedLayer]?.displayName ?? 'Layer'
      : 'Select a data layer';
    const peekTitle = scenario.activeScenario
      ? scenario.activeScenario.name
      : activeLayerLabel;
    const peekEyebrow = scenario.activeScenario
      ? `${mill.state} · Scenario`
      : `${mill.state} · ${selectedDriveTime} min shed`;
    return (
      <MobileBottomSheet
        peekEyebrow={peekEyebrow}
        peekTitle={peekTitle}
        expanded={mobileSheetExpanded}
        onExpandedChange={onMobileSheetExpandedChange}
      >
        <ChromeBar />
        {body}
      </MobileBottomSheet>
    );
  }

  // Desktop: collapsed slim rail or expanded side-rail.
  if (isCollapsed) {
    return (
      <>
        <div className="relative h-full w-12 bg-figma-panel border-r border-white/10 flex-shrink-0 transition-all duration-300">
          <ChromeBar />
        </div>
        <PanelToggleButton onClick={onToggleCollapse} mode="show" />
      </>
    );
  }

  return (
    <>
      <div className="relative w-[420px] lg:w-[460px] flex-none flex-shrink-0 h-full min-h-0 bg-figma-panel border-r border-white/10 flex flex-col transition-all duration-300">
        <ChromeBar />
        <div className="flex-1 overflow-y-auto">{body}</div>
      </div>

      <PanelToggleButton onClick={onToggleCollapse} mode="hide" />
    </>
  );
};
